import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { useHubToast } from "@/components/ui/HubToast";
import { apiFetch } from "@/lib/api";

export type PersonHubRole = { id: number; name: string | null };

function roleLabel(role: PersonHubRole): string {
  const name = role.name?.trim();
  return name && name.length > 0 ? name : `Role ${role.id}`;
}

export function PersonHubRoles({
  staffId,
  hubUserId,
  roles,
  canEditHubRoles,
  hubRoleCatalog,
  quickbooksTimeClockMatched,
}: {
  staffId: number;
  hubUserId: string | null;
  roles: PersonHubRole[];
  canEditHubRoles: boolean;
  hubRoleCatalog: PersonHubRole[] | null;
  quickbooksTimeClockMatched: boolean;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    setSelected(roles.map((role) => role.id));
  }, [roles]);

  const savedKey = roles
    .map((role) => role.id)
    .sort((left, right) => left - right)
    .join(",");
  const draftKey = [...selected].sort((left, right) => left - right).join(",");
  const dirty = savedKey !== draftKey;
  const canEdit = canEditHubRoles && hubUserId != null && hubRoleCatalog != null;

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ roles: PersonHubRole[] }>(`/api/people/staff/${staffId}/hub-roles`, {
        method: "PUT",
        body: { roleIds: selected },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["people", "staff", staffId] });
      showToast({ message: "Hub roles saved.", tone: "success" });
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const readOnlyText =
    hubUserId == null
      ? "No Hub login"
      : roles.length === 0
        ? "Hub login with no role"
        : roles.map(roleLabel).join(", ");

  return (
    <HubSettingsSection
      title="Hub role"
      hoverHint="Hub roles decide which modules this person can open. People access does not include permission to change them. Only an integrator administrator can edit roles here."
      footer={
        canEdit ? (
          <HubSaveButton
            dirty={dirty}
            disabled={selected.length === 0}
            blockedHint="Select at least one Hub role."
            isLoading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save Hub roles
          </HubSaveButton>
        ) : null
      }
    >
      {canEdit ? (
        <ul className="space-y-2">
          {hubRoleCatalog.map((role) => (
            <li key={role.id}>
              <label className="inline-flex items-center gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={selected.includes(role.id)}
                  onChange={(event) => {
                    setSelected((current) =>
                      event.target.checked ? [...current, role.id] : current.filter((id) => id !== role.id),
                    );
                  }}
                />
                {roleLabel(role)}
              </label>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-800">{readOnlyText}</p>
      )}
      <p className="mt-3 text-sm text-slate-700">
        {quickbooksTimeClockMatched
          ? "QuickBooks Time clock is matched, so clock in and clock out on the hub can punch that user."
          : "QuickBooks Time clock is not matched. Clock in on the hub needs the QuickBooks Time user set up with the personal email on this record."}
      </p>
    </HubSettingsSection>
  );
}
